import { formatDate, formatPercentage } from '@if/core/utils'
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded'
import { Box, Button, Tooltip, Typography } from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { FormatPromoCode } from '@src/containers/App/utils/Formatter'
import { Merchant } from '@src/data/types/Merchant'
import { MerchantPaymentPlan } from '@src/data/types/MerchantPaymentPlan'
import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ApplicableMerchantPaymentPlan } from '@src/data/types/ApplicableMerchantPaymentPlan'
import CreateMerchantPaymentPlanDialog from './CreateMerchantPaymentPlanDialog'

interface MerchantPaymentPlansPanelProps {
  merchant: Merchant
  merchantPaymentPlans: MerchantPaymentPlan[]
  merchantPaymentPlansToCreate: ApplicableMerchantPaymentPlan[]
  onMerchantPaymentPlanAdded: (dto: ApplicableMerchantPaymentPlan) => void
}

const MerchantPaymentPlansPanel: FC<MerchantPaymentPlansPanelProps> = ({
  merchant,
  merchantPaymentPlans,
  merchantPaymentPlansToCreate,
  onMerchantPaymentPlanAdded,
}) => {
  const merchantFinancingCompanyId = merchant.financingCompanyId

  const { t } = useTranslation()

  const [openCreateDialog, setOpenCreateDialog] = useState(false)

  interface GridRowModel {
    id: string
    promoCode: string
    borrowerFeeRate: number
    merchantFeeRate: number
    activeFrom: Date
    expiresOn?: Date | null
    highlight?: boolean
  }

  const columns = useMemo(
    (): GridColDef<GridRowModel>[] => [
      {
        field: 'promoCode',
        headerName: t('merchant.promo.promoCode'),
        headerAlign: 'center',
        align: 'center',
        flex: 2,
        valueFormatter: (value) => FormatPromoCode(value),
      },
      {
        field: 'borrowerFeeRate',
        headerName: t('merchant.ratesAndFees.adminFeeCustomer'),
        valueFormatter: formatPercentage,
        headerAlign: 'center',
        align: 'center',
        flex: 1,
      },
      {
        field: 'merchantFeeRate',
        headerName: t('merchant.ratesAndFees.adminFeeMerchant'),
        valueFormatter: formatPercentage,
        headerAlign: 'center',
        align: 'center',
        flex: 1,
      },
      {
        field: 'activeFrom',
        headerName: t('merchant.promo.activeFrom'),
        valueFormatter: formatDate,
        headerAlign: 'center',
        align: 'center',
        width: 120,
      },
      {
        field: 'expiresOn',
        headerName: t('merchant.promo.expiresOn'),
        valueFormatter: formatDate,
        headerAlign: 'center',
        align: 'center',
        width: 120,
      },
    ],
    [t],
  )

  const rows: GridRowModel[] = useMemo(() => {
    const augmentedPlansToCreate = merchantPaymentPlansToCreate.map((data, i) => ({
      ...data,
      id: `__id_${i}`,
      highlight: true,
    }))
    const augmentedPlans = merchantPaymentPlans
    return [...augmentedPlansToCreate, ...augmentedPlans]
  }, [merchantPaymentPlansToCreate, merchantPaymentPlans])

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ mb: 2 }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6" gutterBottom>
            {t('merchantPaymentPlansPanel.merchantPaymentPlans')}
          </Typography>
          <Tooltip title={t('merchantPaymentPlansPanel.createMerchantPaymentPlan')}>
            <Button variant="contained" color="primary" onClick={() => setOpenCreateDialog(true)}>
              <AddCircleOutlineRoundedIcon />
            </Button>
          </Tooltip>
        </Box>

        <DataGrid
          rows={rows}
          columns={columns}
          getRowClassName={(params) => (params.row.highlight ? 'yellow-outline-highlight' : '')}
          disableRowSelectionOnClick
        />

        <CreateMerchantPaymentPlanDialog
          open={openCreateDialog}
          handleClose={() => setOpenCreateDialog(false)}
          merchantFinancingCompanyId={merchantFinancingCompanyId}
          onMerchantPaymentPlanAdded={onMerchantPaymentPlanAdded}
        />
      </Box>
    </Box>
  )
}

export default MerchantPaymentPlansPanel
